import { Comparator } from '@/common/const';
import { Maybe, Nullable, Nullish, ScanSessionJSON } from '@/common/types';
import { CriteriaCategory, DemographicsCategory } from './CriteriaCategory.enum';
import { CriteriaUnit } from './CriteriaUnit.enum';
import { FactorCategory } from './FactorCategory.enum';
import { OutcomeCategory } from './OutcomeCategory.enum';
import { Sex } from './Sex.enum';

export interface ScanSessionOutcomeData {
    createdDate: string;
    practitionerName: string;
    assessmentName: string;
    session?: ScanSessionJSON;
    participantDetails: {
        uid: Nullable<string>;
        archivedDate: Nullable<string>;
    };
}

export interface OutcomeFactor {
    category: FactorCategory;
    description: Nullable<string>;
    criteria: FactorCriteria[];
    shortDescription: Nullable<string>;
}

export type FactorResult = Omit<OutcomeFactor, 'criteria'>;

export type FactorCriteriaValue = number | Sex;
interface FactorCriteriaCondition {
    comparator: Comparator;
    value: FactorCriteriaValue;
}

export interface FactorCriteria {
    type: CriteriaCategory;
    units: Nullable<CriteriaUnit>;
    conditions: FactorCriteriaCondition[];
}

export interface AssessmentOutcome {
    type: OutcomeCategory;
    assessmentId: string;
    demographicsRequired: Nullable<DemographicsCategory[]>;
    factors: OutcomeFactor[];
    source: string;
    title?: Nullish<string>;
    description?: Nullish<string>;
    // Either resultDefinitionId is set, or the left/right pair is — never both. Sided
    // outcomes use the pair; the unused side of the union is explicitly null.
    resultDefinitionId?: Nullish<string>;
    leftResultDefinitionId?: Nullish<string>;
    rightResultDefinitionId?: Nullish<string>;
}

export interface ParticipantDemographics {
    age: Maybe<{ value: number; type: DemographicsCategory.AGE }>;
    sex: Maybe<{ value: Sex; type: DemographicsCategory.SEX }>;
}

export interface OutcomeBoxData {
    factorResultCategory: Maybe<FactorCategory>;
    name: string;
    value: string;
    units: string;
    label: string;
}

export const UNSPECIFIED = 'UNSPECIFIED';

export type MaybeSex = Sex | typeof UNSPECIFIED;

export type OutcomeFactorTableData = Map<
    MaybeSex,
    {
        rows: OutcomeFactorTableRowData[];
    }
>;

export interface OutcomeFactorTableRowData {
    ageRange: string;
    values: { value: string; unit: Nullable<string> }[];
    isResultRow: boolean;
    factorColor?: string;
}
